/**
 * @fileoverview What every right-side panel is made of, so the one panel looks
 * the same wherever it opens — the chat canvas, the homepage's update and task
 * details, the Tasks board.
 *
 * The geometry is the artifact's `.art`: it opens at 900px, is never dragged
 * under 480px and never takes the column beside it under 480px either; it
 * rests as an island on the page's ground — 5px clear of the top, bottom and
 * right, 16px radius, hairline, two-layer shadow — and expanded it fills the
 * page column beside the app sidebar, flat and square (`.art-full`).
 *
 * The header is the artifact's `.phead`: a 52px strip at 18px padding,
 * carrying either a title or a breadcrumb, then the panel's own controls.
 *
 * The two hosts differ in one thing only, which is why the island is spelled
 * twice: the docked details panel is a full-width overlay below `lg` and an
 * island past it, so it needs the same declarations `lg:`-gated, and Tailwind
 * has to see those variants written out.
 */
import type { ReactNode } from 'react';

/** What a panel opens at before anyone drags it (artifact ART_W). */
export const PANEL_WIDTH_PX = 900;
/** The narrowest a panel can be dragged: its content still has to read. */
export const PANEL_MIN_WIDTH_PX = 480;
/** What the column beside the panel keeps, whatever the panel asks for. */
export const PANEL_MIN_SIBLING_WIDTH_PX = 480;

/**
 * The island (artifact `.art`), for a host that is an island at every width.
 *
 * 5px clear of the sheet, not the 14 the artifact measures: `.art` is fixed to
 * the window there, while here the panel sits inside a content sheet that is
 * itself inset 9px — so the two gaps used to add up to a channel twice the
 * width the design draws.
 */
export const PANEL_ISLAND_CLASS =
  'my-[5px] mr-[5px] overflow-hidden rounded-2xl border border-border '
  + 'shadow-[0_1px_2px_rgba(0,0,0,0.04),0_12px_32px_rgba(0,0,0,0.06)]';

/**
 * The same island, `lg:`-gated, for the docked details panel — which is a
 * full-width overlay below `lg` and only becomes an island past it. Kept
 * beside its twin above so the two spellings cannot drift.
 */
export const PANEL_ISLAND_LG_CLASS =
  'lg:my-[5px] lg:mr-[5px] lg:overflow-hidden lg:rounded-2xl lg:border lg:border-border '
  + 'lg:shadow-[0_1px_2px_rgba(0,0,0,0.04),0_12px_32px_rgba(0,0,0,0.06)]';

/**
 * Expanded (artifact `.art-full`): the page column beside the app sidebar,
 * flat and square. Positioned against the host's own row, so every host that
 * opens a panel has to be `relative`.
 */
export const PANEL_EXPANDED_CLASS = 'absolute inset-0 z-30 w-full';

/** The 52px header strip (artifact `.phead`). */
export const PANEL_HEADER_CLASS =
  'flex h-[52px] shrink-0 items-center gap-2 border-b border-black/[0.08] bg-card px-[18px]';

/** The view a panel has layered something over, named in the breadcrumb. */
export interface PanelParentView {
  /** What that view is called — the panel's own title while it was showing. */
  label: string;
  onBack: () => void;
}

/**
 * The artifact's fullscreen glyphs: corner brackets pointing out to expand,
 * and pointing in to exit.
 */
export function PanelFullscreenIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="size-4 fill-none stroke-current stroke-[1.8]"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path
        d={
          expanded
            ? 'M3 8h5V3M21 8h-5V3M16 21v-5h5M8 21v-5H3'
            : 'M8 3H3v5M16 3h5v5M21 16v5h-5M3 16v5h5'
        }
      />
    </svg>
  );
}

interface PanelHeaderTitleProps {
  title: string;
  /** Given by a host that has layered a second thing over a first. */
  parent?: PanelParentView;
  /** A second line under the title, for what the title cannot say itself. */
  subtitle?: ReactNode;
}

/**
 * What the header says the panel is showing.
 *
 * Alone, the title. Over a parent view, the artifact's `.tk-crumb` breadcrumb:
 * the parent in quiet ink and clickable, a chevron, then what is open — one
 * line that says where the reader is and how to get back, which is why no back
 * arrow sits beside it.
 */
export function PanelHeaderTitle({ title, parent, subtitle }: PanelHeaderTitleProps) {
  return (
    <div className="min-w-0 flex-1">
      {parent ? (
        <span className="flex min-w-0 items-center gap-1.5 overflow-hidden text-[13px]">
          <button
            type="button"
            onClick={parent.onBack}
            title={parent.label}
            className="max-w-[180px] truncate text-[#9A9A9A] transition-colors hover:text-[#181818]"
          >
            {parent.label}
          </button>
          <span aria-hidden="true" className="shrink-0 text-[#9A9A9A]">
            &rsaquo;
          </span>
          <span className="truncate font-medium text-[#181818]">{title}</span>
        </span>
      ) : (
        <div className="truncate text-[13.5px] font-semibold tracking-[-0.01em] text-[#181818]">
          {title}
        </div>
      )}
      {subtitle ? (
        <div className="truncate text-[11px] text-[#9A9A9A]">{subtitle}</div>
      ) : null}
    </div>
  );
}
